import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

/**
 * CountUp – API compatibile con React Bits @react-bits/CountUp-JS-CSS
 * from, to, separator, direction, duration (secondi), className, startCounting
 */
function CountUp({
  from = 0,
  to = 100,
  separator = '',
  decimals = 0,
  direction = 'up',
  duration = 1,
  className = 'count-up-text',
  startCounting = false,
}) {
  const endValue = direction === 'up' ? Math.max(from, to) : Math.min(from, to);
  const [displayValue, setDisplayValue] = useState(from);
  const hasStarted = useRef(false);

  const formatWithSeparator = (num) => {
    const formatted =
      decimals > 0
        ? num.toFixed(decimals).replace('.', ',')
        : String(Math.round(num));
    if (!separator || decimals > 0) return formatted;
    return formatted.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  };

  useEffect(() => {
    if (!startCounting) return;
    if (hasStarted.current) return;
    hasStarted.current = true;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setDisplayValue(endValue);
      return;
    }

    const start = from;
    const end = endValue;
    const durationMs = duration * 1000;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = 1 - (1 - progress) ** 2;
      const current = start + (end - start) * eased;
      setDisplayValue(current);
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [from, to, direction, duration, startCounting, endValue]);

  return (
    <motion.span className={className} initial={false} aria-live="polite">
      {formatWithSeparator(displayValue)}
    </motion.span>
  );
}

export default CountUp;
